import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

export interface StorageHistoryState {
    teamId?: number
    histories: StorageHistoryProps[]
    finished: boolean
    loading: boolean
    lastDate?: string
}

export interface StorageHistoryProps {
    teamId: number
    date: string
    jandiFileCount: number
    jandiFileSize: number
    albumFileCount: number
    albumFileSize: number
    driveFileCount: number
    driveFileSize: number
    offsetTime: string
    createdAt: string
    limitSize: number
}

export const initialState: StorageHistoryState = {
    histories: [],
    finished: false,
    loading: false
};

let pending = false;

export const searchStorageHistories = createAsyncThunk<StorageHistoryProps[], number, { state: RootState }>(
    'storageHistory/search',
    async (teamId: number, thunkAPI) => {
        if (!teamId) return;
        const state = thunkAPI.getState();
        const { lastDate, finished } = state.storageHistory;

        if (finished || pending) return;

        pending = true;

        let url = `api/teams/${teamId}/storage-histories`;
        if (lastDate) url += `?date=${lastDate}`;

        try {
            const response = await apiClient.get<StorageHistoryProps[]>(url);
            return response.data;
        } finally {
            pending = false;
        }
    }
)

const storageHistorySlice = createSlice({
    name: "storageHistory",
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchStorageHistories.pending, (state) => {
                state.loading = true;
            })
            .addCase(searchStorageHistories.fulfilled, (state, action) => {
                state.loading = false;
                if (!action.payload) return;
                const histories = action.payload;

                state.histories = state.histories.concat(histories);

                const length = histories.length;
                if (length > 0) {
                    const last = histories[length - 1];
                    state.lastDate = last.date;
                }

                if (length === 0) state.finished = true;
            })
    },
    reducers: {
        refresh: () => {
            pending = false;
            return initialState;
        },
    }
})

export const {
    refresh
} = storageHistorySlice.actions;
export default storageHistorySlice.reducer;
